import React, { useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { Button } from "../ui/Button";
import {
    MAX_KEYWORDS,
    parseKeywordInput,
    resolveEffectiveKeywords,
} from "./keywords";

const Tag = ({ label, onRemove }) => (
    <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs font-medium text-ktx-purple">
        {label}
        <button
            type="button"
            onClick={onRemove}
            className="hover:opacity-70"
            aria-label={`Remove ${label}`}
        >
            <X className="h-3 w-3" aria-hidden />
        </button>
    </span>
);

const DroppedChips = ({ keywords }) => (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
        {keywords.map((keyword) => (
            <span
                key={keyword.toLowerCase()}
                className="inline-flex items-center rounded-full border border-red-200 bg-white px-2 py-0.5 text-xs font-medium text-red-800"
            >
                {keyword}
            </span>
        ))}
    </div>
);

const countTone = (count) => {
    if (count < 50) {
        return {
            text: "text-green-700",
            box: "border-green-200 bg-green-50 text-green-800",
        };
    }
    if (count > 90) {
        return {
            text: "text-red-700",
            box: "border-red-200 bg-red-50 text-red-800",
        };
    }
    return {
        text: "text-amber-700",
        box: "border-amber-200 bg-amber-50 text-amber-900",
    };
};

const ActiveDictionaryCount = ({ count, className = "" }) => (
    <span className={className}>
        <span className="font-semibold">
            {count}/{MAX_KEYWORDS}
        </span>{" "}
        words in the active dictionary
    </span>
);

const EffectiveTotal = ({ ownKeywords, ancestorScopes, scopeLabel }) => {
    if (ancestorScopes.length === 0) return null;

    const { scopes } = resolveEffectiveKeywords([
        ...ancestorScopes,
        { label: scopeLabel, keywords: ownKeywords },
    ]);
    const losing = scopes.filter((scope) => scope.dropped > 0);
    if (losing.length === 0) return null;

    return (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            <p className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>These keywords would be dropped from the merged dictionary:</span>
            </p>
            {losing.map((scope) => (
                <div key={scope.label} className="mt-2">
                    <p className="text-xs font-semibold">
                        {scope.label} ({scope.dropped})
                    </p>
                    <DroppedChips keywords={scope.droppedKeywords} />
                </div>
            ))}
        </div>
    );
};

export const DescendantDropWarning = ({ impacts }) => {
    if (!impacts?.length) return null;

    return (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            <p className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                    Saving these keywords would push lower-level dictionaries
                    past {MAX_KEYWORDS}. These words would be dropped:
                </span>
            </p>
            {impacts.map((impact) => (
                <div key={impact.label} className="mt-2">
                    <p className="text-xs font-semibold">
                        {impact.label} ({impact.keywords.length})
                    </p>
                    <DroppedChips keywords={impact.keywords} />
                </div>
            ))}
        </div>
    );
};

const KeywordEditor = ({
    scopeLabel,
    keywords,
    onChange,
    ancestorScopes = [],
    onSave,
    onClear,
    saving = false,
    dirty = false,
    savedAt = null,
    savedBy = null,
    existsUpstream = false,
}) => {
    const [input, setInput] = useState("");

    const activeCount =
        ancestorScopes.length === 0
            ? keywords.length
            : resolveEffectiveKeywords([
                  ...ancestorScopes,
                  { label: scopeLabel, keywords },
              ]).merged.length;
    const overOwnCap = keywords.length > MAX_KEYWORDS;

    const addFromInput = () => {
        const additions = parseKeywordInput(input);
        if (additions.length === 0) return;
        const existing = new Set(keywords.map((word) => word.toLowerCase()));
        const fresh = [];
        for (const keyword of additions) {
            const key = keyword.toLowerCase();
            if (existing.has(key)) continue;
            existing.add(key);
            fresh.push(keyword);
        }
        setInput("");
        if (fresh.length > 0) onChange([...keywords, ...fresh]);
    };

    const removeKeyword = (target) =>
        onChange(
            keywords.filter(
                (keyword) => keyword.toLowerCase() !== target.toLowerCase(),
            ),
        );

    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.key === ",") {
            event.preventDefault();
            addFromInput();
            return;
        }
        if (event.key === "Backspace" && !input && keywords.length > 0) {
            onChange(keywords.slice(0, -1));
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900">
                    {scopeLabel} keywords
                </h3>
                <ActiveDictionaryCount
                    count={activeCount}
                    className={`text-xs font-medium ${countTone(activeCount).text}`}
                />
            </div>

            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 shadow-sm focus-within:border-ktx-purple focus-within:ring-2 focus-within:ring-ktx-purple">
                {keywords.map((keyword) => (
                    <Tag
                        key={keyword.toLowerCase()}
                        label={keyword}
                        onRemove={() => removeKeyword(keyword)}
                    />
                ))}
                <input
                    type="text"
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={addFromInput}
                    placeholder={
                        keywords.length === 0
                            ? "Type a name or term, then press Enter"
                            : "Add another..."
                    }
                    className="min-w-[12rem] flex-1 border-0 bg-transparent py-0.5 text-sm text-gray-900 focus:outline-none focus:ring-0"
                />
            </div>

            {overOwnCap && (
                <p className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>
                        A single scope cannot exceed {MAX_KEYWORDS} keywords.
                        Remove {keywords.length - MAX_KEYWORDS} to save.
                    </span>
                </p>
            )}

            <EffectiveTotal
                ownKeywords={keywords}
                ancestorScopes={ancestorScopes}
                scopeLabel={scopeLabel}
            />

            <div className="flex flex-wrap items-center gap-3">
                <Button
                    type="button"
                    onClick={onSave}
                    disabled={saving || overOwnCap || !dirty}
                    className="bg-ktx-purple text-white hover:bg-purple-700"
                >
                    {saving && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    )}
                    Save keywords
                </Button>
                {existsUpstream && (
                    <button
                        type="button"
                        onClick={onClear}
                        disabled={saving}
                        className="text-xs font-medium text-gray-500 hover:text-red-600 hover:underline disabled:opacity-50"
                    >
                        Delete this dictionary
                    </button>
                )}
                {dirty && !saving && (
                    <span className="text-xs text-amber-700">Unsaved changes</span>
                )}
                {!dirty && savedAt && (
                    <span className="text-xs text-gray-500">
                        Last updated {new Date(savedAt).toLocaleString()}
                        {savedBy ? ` by ${savedBy}` : ""}
                    </span>
                )}
            </div>
        </div>
    );
};

export default KeywordEditor;
